import type { Adequation, CritereAdequation, EtatCritere } from '../../lib/useMatching';

/**
 * Les quatre dimensions du matching, lisibles d'un coup d'œil.
 *
 * Le score seul ne dit pas d'où il vient : 63/100 peut être un dossier bien
 * renseigné mais moyennement compatible, ou un dossier dont la moitié des
 * critères n'a jamais été saisie. Cette bande répond avant qu'on déplie le
 * détail, et elle distingue une disponibilité déduite d'une disponibilité
 * déclarée — le chargé de relation entreprise n'a pas à connaître le
 * fonctionnement du moteur pour voir que la donnée n'a pas été recueillie.
 */

const DIMENSIONS: { ligne: string; court: string }[] = [
  { ligne: 'Metier', court: 'Métier' },
  { ligne: 'Formation', court: 'Formation' },
  { ligne: 'Disponibilite', court: 'Dispo.' },
  { ligne: 'Mobilite', court: 'Mobilité' },
];

/** Repli sur les propositions enregistrées avant l'introduction des états. */
function etatCritere(critere: CritereAdequation): EtatCritere {
  if (critere.etat) return critere.etat;
  return critere.points === null ? 'NON_RENSEIGNE' : 'MESURE';
}

function apparence(critere: CritereAdequation): { classe: string; valeur: string; suffixe: string } {
  const etat = etatCritere(critere);
  if (etat === 'NON_EVALUABLE') {
    return {
      classe: 'border-gray-200 bg-gray-50 text-gray-500',
      valeur: '—',
      suffixe: 'non évaluable',
    };
  }
  if (etat === 'NON_RENSEIGNE') {
    return {
      classe: 'border-orange-200 bg-orange-50 text-orange-700',
      valeur: `0/${critere.maximum}`,
      suffixe: 'non renseigné',
    };
  }
  if (critere.source === 'DEDUITE') {
    return {
      classe: 'border-amber-200 bg-amber-50 text-amber-800',
      valeur: `${critere.points ?? 0}/${critere.maximum}`,
      suffixe: 'déduite',
    };
  }
  return {
    classe: 'border-gray-200 bg-white text-codialis-navy',
    valeur: `${critere.points ?? 0}/${critere.maximum}`,
    suffixe: '',
  };
}

export function ResumeCriteres({
  adequation,
  libelles = {},
}: {
  adequation: Adequation;
  /** Libellés rendus par l'API, indexés par le nom de la ligne de détail. */
  libelles?: Record<string, string>;
}): JSX.Element {
  return (
    <ul className="flex flex-wrap gap-1" aria-label="Détail des quatre critères">
      {DIMENSIONS.map(({ ligne, court }) => {
        const critere = adequation.criteres.find((entree) => entree.critere === ligne);
        if (!critere) return null;
        const { classe, valeur, suffixe } = apparence(critere);
        const nom = libelles[ligne] ?? court;
        return (
          <li
            key={ligne}
            title={`${nom} · ${critere.motif}`}
            className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${classe}`}
          >
            <span className="font-bold">{court}</span> {valeur}
            {suffixe && <span className="ml-1 font-normal italic">{suffixe}</span>}
          </li>
        );
      })}
    </ul>
  );
}
